import { useEffect, useRef, useState } from 'react';
import type { Order, Ticket } from '@loctopus/ticketing-sdk';
import { ErrorSummary } from '@loctopus/ui';
import { useAppContext } from '../lib/AppContext';
import { useRouter } from '../router';
import { Badge, Button, Card, ErrorBanner, Spinner } from '../components/ui';
import { toParsedError } from '../lib/errorDisplay';
import { formatMoney } from '../lib/format';

const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 30000;

type LoadState =
  | { status: 'confirming' }
  // Plain, already-specific application messages (order status, poll timeout) - not an API
  // error, so these stay as-is rather than being forced through parseApiError.
  | { status: 'info_error'; message: string }
  // A real thrown error from an SDK call - gets the full shared treatment (request ID, etc).
  | { status: 'api_error'; error: ReturnType<typeof toParsedError> }
  | { status: 'ready'; order: Order; tickets: Ticket[] };

/**
 * MASTER_PROMPT.md: tickets are only issued once an order reaches 'paid'/'fulfilled' via a
 * confirmed provider webhook, never on the client-side redirect alone - so this page polls
 * `getOrder` for a little while rather than assuming payment success means tickets already exist.
 *
 * `accessToken` (task #4): the SAME guest-buyer access token `router.tsx`'s `checkout` route
 * already carries, now also carried by the `order` route (see CheckoutPage.tsx, which forwards
 * `session.accessToken` here once payment succeeds). Passed through to `getOrder`/`getTicket`
 * below AND used to build the receipt-download link.
 *
 * REPORTED, NOT FIXED (outside apps/checkout's ownership - found live while wiring this up):
 * 1. `packages/sdk/src/client.ts`'s `getOrder`/`getTicket` accept `ReadOptions.accessToken` in
 *    their type signature but never forward it to the actual HTTP request (unlike
 *    `getCheckoutSession`, which sends `query: { accessToken: options?.accessToken }`). Confirmed
 *    live against `apps/storefront-api/src/routes/public/orders.ts` and `tickets.ts`: both call
 *    `requireBuyerAccessToken`, which THROWS `unauthenticated` when `?accessToken=` is missing for
 *    any non-secret-key caller - and `apps/checkout` only ever holds a public key (src/lib/sdk.ts).
 *    Net effect: against the real backend, this page's `getOrder`/`getTicket` calls below 401 for
 *    every guest buyer today, independent of anything in apps/checkout - `packages/sdk` needs the
 *    same `query: { accessToken }` treatment `getCheckoutSession` already has.
 * 2. Even if (1) were fixed, `GET /v1/tickets/:id` requires a TICKET-scoped access token
 *    (`apps/storefront-api/src/lib/buyerAccessToken.ts`'s `issueBuyerAccessToken(secret, 'ticket',
 *    ticketId)` - HMAC keyed on the ticket id, NOT the order id). No endpoint anywhere issues this
 *    per-ticket token to the browser; only an ORDER-scoped token is ever returned
 *    (`createCheckoutSession`'s response). So `client.getTicket(id, { accessToken })` below is
 *    passed the order's token for lack of any other option, but it cannot actually satisfy a
 *    ticket-scoped check - this is a backend design gap (apps/storefront-api), not something
 *    apps/checkout can work around on its own.
 */
export function ConfirmationPage({
  orderId,
  accessToken,
}: {
  orderId: string;
  accessToken?: string;
}) {
  const { client, config } = useAppContext();
  const { navigate } = useRouter();
  const [state, setState] = useState<LoadState>({ status: 'confirming' });
  const startedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    async function poll() {
      try {
        const order = await client.getOrder(orderId, { accessToken });
        if (cancelled) return;
        if (
          order.status === 'paid' ||
          order.status === 'fulfilled' ||
          order.status === 'partially_refunded'
        ) {
          // REAL BUG FOUND AND FIXED: each ticket needs its OWN accessToken
          // (order.ticketAccessTokens[id]), not the order's - they're different HMAC inputs
          // ('ticket:<id>' vs 'order:<id>') and are never interchangeable server-side
          // (apps/storefront-api's requireBuyerAccessToken). Reusing the order's token here
          // always failed with "does not match this ticket".
          const tickets = await Promise.all(
            order.ticketIds.map((id) =>
              client.getTicket(id, { accessToken: order.ticketAccessTokens[id] }),
            ),
          );
          if (!cancelled) setState({ status: 'ready', order, tickets });
          return;
        }
        if (
          order.status === 'cancelled' ||
          order.status === 'refunded' ||
          order.status === 'disputed'
        ) {
          if (!cancelled) {
            setState({
              status: 'info_error',
              message: `This order's status is "${order.status}" - no tickets are available for it.`,
            });
          }
          return;
        }
        if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
          if (!cancelled) {
            setState({
              status: 'info_error',
              message:
                'Your payment is still being confirmed by the payment provider. This can take a minute - refresh this page shortly, or check your email for confirmation.',
            });
          }
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'api_error', error: toParsedError(err) });
        }
      }
    }

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, orderId, accessToken]);

  if (state.status === 'confirming') {
    return <Spinner label="Confirming your payment…" />;
  }
  if (state.status === 'info_error') {
    return <ErrorBanner message={state.message} />;
  }
  if (state.status === 'api_error') {
    return <ErrorSummary error={state.error} />;
  }

  const { order, tickets } = state;

  // Task #4 (receipt download). No SDK method or backend route exists for this yet (checked
  // live: apps/storefront-api/src/routes/public has no "receipt" route at all) - ASSUMED shape:
  //   GET {baseUrl}/v1/orders/{orderId}/receipt?accessToken=<token>
  // same guest-access-token query-param convention `GET /v1/orders/:id` already uses
  // (apps/storefront-api/src/lib/buyerAccessToken.ts), returning the receipt file itself (e.g. a
  // PDF) for the browser to open/download directly - built as a plain `<a href>` straight from
  // `config.baseUrl`, the same pattern this page already uses for `ticket.wallet.apple`/`.google`
  // links below, rather than adding a method to packages/sdk (a shared package outside this app's
  // ownership). See this component's top doc comment for why this accessToken may not actually be
  // accepted by the real backend yet even once the route exists.
  const receiptUrl = `${config.baseUrl.replace(/\/+$/, '')}/v1/orders/${encodeURIComponent(order.id)}/receipt${
    accessToken ? `?accessToken=${encodeURIComponent(accessToken)}` : ''
  }`;

  return (
    <div>
      <h1 className="font-heading text-2xl font-semibold text-slate-800 mb-1">You're going! 🎉</h1>
      <p className="text-slate-500 mb-6">
        A confirmation with your tickets was sent to <strong>{order.buyer.email}</strong>.
      </p>

      <Card title="Order summary" className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-slate-500">Order #{order.id}</span>
          <Badge
            tone={order.status === 'paid' || order.status === 'fulfilled' ? 'success' : 'warning'}
          >
            {order.status}
          </Badge>
        </div>
        <ul className="text-sm text-slate-600 space-y-1">
          {order.items.map((item) => (
            <li key={item.id} className="flex justify-between">
              <span>
                {item.quantity} × {item.ticketTypeName ?? item.addOnName ?? 'Item'}
              </span>
              <span>{formatMoney(item.amount)}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-between font-semibold text-slate-800 mt-2 pt-2 border-t border-slate-100">
          <span>Total paid</span>
          <span>{formatMoney(order.total)}</span>
        </div>
      </Card>

      <div className="space-y-4">
        {tickets.map((ticket) => (
          <Card key={ticket.id} title={ticket.ticketTypeName ?? 'Ticket'}>
            <div className="flex items-center gap-4">
              <img
                src={ticket.qrCodeUrl}
                alt={`QR code for ticket ${ticket.id}`}
                className="h-28 w-28 shrink-0"
              />
              <div>
                {ticket.seatLabel && (
                  <p className="text-sm text-slate-600">Seat: {ticket.seatLabel}</p>
                )}
                {ticket.holderName && (
                  <p className="text-sm text-slate-600">Holder: {ticket.holderName}</p>
                )}
                <Badge tone="info">{ticket.status}</Badge>
                {ticket.wallet?.apple && (
                  <div className="mt-2">
                    <a href={ticket.wallet.apple} className="text-sm text-brand-primary underline">
                      Add to Apple Wallet
                    </a>
                  </div>
                )}
                {ticket.wallet?.google && (
                  <div className="mt-1">
                    <a href={ticket.wallet.google} className="text-sm text-brand-primary underline">
                      Add to Google Wallet
                    </a>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Button variant="secondary" onClick={() => navigate({ name: 'events' })}>
          Browse more events
        </Button>
        <a
          href={receiptUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-brand-primary underline focus-ring"
          data-testid="download-receipt-link"
        >
          Download receipt
        </a>
      </div>

      <p className="mt-6 text-xs text-slate-400">
        Didn't get the email? Check your spam folder - resending is handled by the event organizer,
        not this page.
      </p>
    </div>
  );
}
